import { type ComputedRef, computed, ref } from 'vue';
import {
  getPathLocale,
  pushKioskLocaleUrl,
  replaceWithCanonicalKioskUrl,
} from '@/services/urlState';
import { addBreadcrumb, setSentryLocale } from '@/telemetry/sentry';
import { DEFAULT_LOCALE, type EntrLocale, setEntrLocale } from '.';

type LocaleChangeSource = 'user_select' | 'session_reset';

export interface EntrLocaleController {
  currentLocale: ComputedRef<EntrLocale>;
  selectLocale: (locale: EntrLocale) => void;
  resetForNewSession: () => void;
  replaceCurrentLocaleUrl: () => void;
}

export function createEntrLocaleController(): EntrLocaleController {
  const launchLocale = getPathLocale();
  const selectedLocale = ref<EntrLocale | null>(null);
  const defaultLocale = computed(() => launchLocale ?? DEFAULT_LOCALE);
  const currentLocale = computed(
    () => selectedLocale.value ?? defaultLocale.value
  );

  function replaceCurrentLocaleUrl(): void {
    replaceWithCanonicalKioskUrl(currentLocale.value);
  }

  function setLocaleForKiosk(
    nextLocale: EntrLocale,
    source: LocaleChangeSource
  ): void {
    const previousLocale = currentLocale.value;
    const resolvedNextLocale =
      source === 'session_reset' ? defaultLocale.value : nextLocale;

    if (source === 'session_reset') {
      selectedLocale.value = null;
    } else {
      selectedLocale.value = resolvedNextLocale;
    }

    setEntrLocale(resolvedNextLocale);

    if (previousLocale !== resolvedNextLocale) {
      setSentryLocale(resolvedNextLocale);
      addBreadcrumb('localization', 'Kiosk locale changed', {
        previousLocale,
        nextLocale: resolvedNextLocale,
        source,
      });
    }

    if (source === 'session_reset') {
      replaceWithCanonicalKioskUrl(resolvedNextLocale);
    } else {
      pushKioskLocaleUrl(resolvedNextLocale);
    }
  }

  return {
    currentLocale,
    selectLocale: (locale) => setLocaleForKiosk(locale, 'user_select'),
    resetForNewSession: () =>
      setLocaleForKiosk(defaultLocale.value, 'session_reset'),
    replaceCurrentLocaleUrl,
  };
}
